<!-- Toolbar -->
<div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
  <div>
    <p class="text-theme-sm text-gray-500 dark:text-gray-400">
      @if (totalCount > 0 || searchTerm) {
        {{ totalCount }} cliente{{ totalCount !== 1 ? 's' : '' }}{{ searchTerm ? ' encontrados' : '' }}.
        Abre a alguien para ver su historial.
      }
    </p>
  </div>
  <div class="flex items-center gap-2">
    <div class="relative flex-1 sm:flex-none">
      <svg class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" fill="none"
        stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="search" [value]="searchTerm" (input)="onSearch($any($event.target).value)"
        placeholder="Nombre, correo o teléfono"
        aria-label="Buscar cliente"
        class="pp-input h-10 w-full pl-9 pr-3 sm:w-72" />
    </div>
    <button type="button" (click)="onCreateClient()"
      class="pp-btn pp-btn--primary inline-flex shrink-0 items-center gap-2" aria-label="Agregar cliente">
      <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      <span class="hidden sm:inline">Agregar cliente</span>
    </button>
  </div>
</div>

<!-- Skeleton loader -->
@if (loading && clients.length === 0) {
  <div class="pp-list" role="status" aria-label="Cargando clientes">
    @for (item of skeletonRows; track $index) {
      <div class="pp-row animate-pulse">
        <div class="flex items-center gap-3" style="grid-area: client">
          <div class="size-10 rounded-full bg-gray-200 dark:bg-gray-800"></div>
          <div class="h-4 w-36 rounded bg-gray-200 dark:bg-gray-800"></div>
        </div>
        <div class="h-3 w-40 rounded bg-gray-100 dark:bg-gray-800/70" style="grid-area: contact"></div>
        <div class="h-3 w-24 rounded bg-gray-100 dark:bg-gray-800/70" style="grid-area: visit"></div>
      </div>
    }
  </div>
}

<!-- Empty state -->
@if (!loading && clients.length === 0) {
  <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center dark:border-gray-700 dark:bg-white/[0.03]">
    @if (searchTerm) {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Nadie coincide con "{{ searchTerm }}"</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Busca por nombre, apellido, correo o teléfono.
      </p>
      <button type="button" (click)="onSearch(''); searchTerm = ''" class="pp-btn pp-btn--ghost">
        Limpiar búsqueda
      </button>
    } @else {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Aún no tienes clientes</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Se agregan solos cuando reservan contigo. También puedes registrar a alguien a mano.
      </p>
      <button type="button" (click)="onCreateClient()" class="pp-btn pp-btn--primary inline-flex items-center gap-2">
        <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Agregar cliente
      </button>
    }
  </div>
}

<!-- Client list -->
@if (clients.length > 0) {
  <div class="pp-list" [class.opacity-60]="loading">
    <div class="pp-row pp-row--head" aria-hidden="true">
      <span style="grid-area: client">Cliente</span>
      <span style="grid-area: contact">Contacto</span>
      <span style="grid-area: visit">Última visita</span>
      <span class="text-right" style="grid-area: count">Citas</span>
    </div>

    <ul>
      @for (client of clients; track client.userId) {
        @let name = fullName(client);
        @let badge = statusBadge(client);
        @let visit = lastVisitLabel(client);
        <li class="pp-row pp-row--item">
          <!-- Identity (the link covers the whole row) -->
          <div class="flex min-w-0 items-center gap-3" style="grid-area: client">
            <span class="pp-avatar" aria-hidden="true">{{ initials(client) }}</span>
            <div class="min-w-0">
              <div class="flex min-w-0 items-center gap-2">
                <a [routerLink]="['/people', client.userId]" class="pp-name pp-row__link truncate">
                  {{ name || client.email }}
                </a>
                @if (badge) {
                  <span class="pp-badge" [attr.data-status]="client.status">{{ badge }}</span>
                }
              </div>
              @if (client.tags?.length) {
                <p class="pp-tags truncate">{{ client.tags!.join(', ') }}</p>
              } @else if (client.phone) {
                <p class="pp-tags truncate tabular-nums md:hidden">{{ client.phone }}</p>
              }
            </div>
          </div>

          <!-- Contact -->
          <div class="pp-contact" style="grid-area: contact">
            @if (client.phone) {
              <span class="tabular-nums">{{ client.phone }}</span>
            }
            @if (client.email) {
              <span class="truncate text-gray-500 dark:text-gray-400">{{ client.email.toLowerCase() }}</span>
            }
          </div>

          <!-- Last visit -->
          <div class="pp-visit" style="grid-area: visit" [attr.data-recency]="recency(client)">
            <span class="pp-visit__dot" aria-hidden="true"></span>
            @if (visit) {
              <span [title]="formatDay(client.lastVisitDate!)">{{ visit }}</span>
            } @else {
              <span>Sin visitas aún</span>
            }
          </div>

          <!-- Appointments -->
          <p class="pp-count" style="grid-area: count">
            {{ client.totalAppointments ?? 0 }}<span class="md:hidden"> cita{{ client.totalAppointments === 1 ? '' : 's' }}</span>
          </p>

          <!-- Actions -->
          <div class="pp-actions flex items-center justify-end gap-1" style="grid-area: actions">
            <button type="button" class="pp-icon-btn" (click)="onEditClient(client.userId)"
              [attr.aria-label]="'Editar a ' + (name || client.email)" title="Editar">
              <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
              </svg>
            </button>
            <button type="button" class="pp-icon-btn pp-icon-btn--danger" (click)="openDelete(client)"
              [attr.aria-label]="'Eliminar a ' + (name || client.email)" title="Eliminar">
              <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
          </div>
        </li>
      }
    </ul>
  </div>

  <!-- Pagination -->
  @if (totalPages > 1) {
    <nav class="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row" aria-label="Paginación">
      <p class="text-theme-sm text-gray-500 tabular-nums dark:text-gray-400">
        {{ (page - 1) * pageSize + 1 }}–{{ page * pageSize > totalCount ? totalCount : page * pageSize }} de {{ totalCount }}
      </p>
      <div class="flex items-center gap-1">
        <button type="button" (click)="onPageChange(page - 1)" [disabled]="page <= 1" class="pp-page" aria-label="Página anterior">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
        </button>
        @for (p of paginationArray; track $index) {
          @if ($index + 1 === page || $index + 1 === 1 || $index + 1 === totalPages || ($index + 1 >= page - 1 && $index + 1 <= page + 1)) {
            <button type="button" (click)="onPageChange($index + 1)" class="pp-page"
              [attr.aria-current]="($index + 1) === page ? 'page' : null">
              {{ $index + 1 }}
            </button>
          } @else if ($index + 1 === page - 2 || $index + 1 === page + 2) {
            <span class="inline-flex size-9 items-center justify-center text-sm text-gray-400" aria-hidden="true">…</span>
          }
        }
        <button type="button" (click)="onPageChange(page + 1)" [disabled]="page >= totalPages" class="pp-page" aria-label="Página siguiente">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
    </nav>
  }
}

<app-modal [open]="open" [title]="modalTitle" [size]="'md'" [bodyComponent]="PeopleAddForm" [closeOnBackdrop]="false"
  [bodyInputs]="modalInputs" (confirm)="onModalConfirm($event)" (close)="open = false" [isSaving]="isSaving"
  confirmButtonText="Guardar">
</app-modal>

<app-confirm-modal [(open)]="confirmOpen" title="Eliminar cliente"
  [subtitle]="toDeleteName ? 'Vas a eliminar a \'' + toDeleteName + '\' y su ficha. Esta acción no se puede deshacer.' : 'Esta acción no se puede deshacer, ¿deseas continuar?'"
  cancelText="No, volver" confirmText="Sí, eliminar"
  variant="danger" [closeOnBackdrop]="true" (confirm)="onConfirmDelete()" (cancel)="toDeleteId = null; toDeleteName = null">
</app-confirm-modal>
